import { faDownToLine } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Menu, MenuItem } from '@mui/material';
import {
  bindMenu,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';

import { exportCsv, exportJson } from '@/utils/export.utils';

import type { ExportButtonProps } from './ExportButton.types';

enum ExportTypes {
  CSV = 'csv',
  JSON = 'json'
}

export const ExportButton = ({
  buttonIcon = faDownToLine,
  buttonLabel = 'Export',
  data,
  datasets,
  json,
  csv,
  fileName,
  variant = 'outlined',
  handleExportCsv,
  mapCsvData,
  mapJsonData
}: ExportButtonProps) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'exportMenu'
  });

  const handleClick = (type: ExportTypes) => {
    if (datasets && datasets.length > 0) {
      // Handle multiple datasets
      if (type === ExportTypes.CSV) {
        if (handleExportCsv) {
          handleExportCsv();
          popupState.close();
          return;
        }
        exportCsv(mapCsvData ? mapCsvData(datasets) : datasets, fileName, true);
      } else {
        exportJson(
          mapJsonData ? mapJsonData(datasets) : datasets,
          fileName,
          true
        );
      }
    } else if (data && data.length > 0) {
      // Handle single dataset
      if (type === ExportTypes.CSV) {
        if (handleExportCsv) {
          handleExportCsv();
          popupState.close();
          return;
        }
        exportCsv(mapCsvData ? mapCsvData(data) : data, fileName, false);
      } else {
        exportJson(mapJsonData ? mapJsonData(data) : data, fileName, false);
      }
    }
    popupState.close();
  };

  if (!csv && !json) return null;

  // Determine if there's any data to export
  const hasData =
    (datasets &&
      datasets.length > 0 &&
      datasets.some((d) => d.data.length > 0)) ||
    (data && data.length > 0);

  // If only one export format is enabled, export directly without showing menu
  const singleFormatOnly = (csv && !json) || (!csv && json);

  const handleDirectExport = () => {
    if (csv && !json) {
      handleClick(ExportTypes.CSV);
    } else if (json && !csv) {
      handleClick(ExportTypes.JSON);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        startIcon={<FontAwesomeIcon size="lg" icon={buttonIcon} />}
        {...(singleFormatOnly
          ? { onClick: handleDirectExport }
          : bindTrigger(popupState))}
        disabled={!hasData}
      >
        {buttonLabel}
      </Button>
      {!singleFormatOnly && (
        <Menu {...bindMenu(popupState)}>
          {csv && (
            <MenuItem onClick={() => handleClick(ExportTypes.CSV)}>
              CSV Format
            </MenuItem>
          )}
          {json && (
            <MenuItem onClick={() => handleClick(ExportTypes.JSON)}>
              JSON Format
            </MenuItem>
          )}
        </Menu>
      )}
    </>
  );
};

export default ExportButton;
